import type { ComponentType, ReactElement, ReactNode } from "react";
import { useContext } from "react";
import { SequenceTimeContext, useCollector } from "./context.js";
import { compileBrowserSetup, type BrowserSetupDefinition } from "./runtime.js";
import type { TimelineBuilder } from "./timeline.js";

export type ComponentTimelineContext = {
  tl: TimelineBuilder;
  scope: string;
  select: (descendant?: string) => string;
  offset: number;
};

export type HFComponentDefinition<Props extends object> = {
  name: string;
  View?: ComponentType<Props>;
  render?: (props: Props, instanceId: string) => ReactNode;
  css?: (scope: string) => string;
  setup?: BrowserSetupDefinition<Props>;
  timeline?: (context: ComponentTimelineContext, props: Props) => void;
};

export type HFComponentProps<Props extends object> = Props & {
  id: string;
};

export function defineHFComponent<Props extends object>(
  definition: HFComponentDefinition<Props>,
): ComponentType<HFComponentProps<Props>> {
  if (!definition.View && !definition.render) {
    throw new Error(`HyperFrames component "${definition.name}" needs View or render.`);
  }

  function DefinedHFComponent(allProps: HFComponentProps<Props>): ReactElement {
    const collector = useCollector();
    const offset = useContext(SequenceTimeContext);
    const { id, ...rest } = allProps;
    const props = rest as Props;
    collector.registerComponent(id);

    const scope = `[data-hf-scope="${id}"]`;
    const select = (descendant = "") => `${scope}${descendant}`;
    const timelineContext = { tl: collector.timeline(offset), scope, select, offset };
    if (definition.setup) {
      collector.registerInitializer(compileBrowserSetup(definition.setup, props, scope, offset));
    }
    definition.timeline?.(timelineContext, props);

    const View = definition.View;
    const content = definition.render ? definition.render(props, id) : View ? <View {...props} /> : null;
    return (
      <div id={id} data-hf-id={id} data-hf-component={definition.name} data-hf-scope={id}>
        {definition.css ? <style>{definition.css(scope)}</style> : null}
        {content}
      </div>
    );
  }

  DefinedHFComponent.displayName = `HF(${definition.name})`;
  return DefinedHFComponent;
}
